import React, { useRef, useState } from "react";
import { Box, Button, Typography, alpha } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { NAVY, GOLD, TEXT_SECONDARY } from "../../theme/tokens";

export interface UploadBoxProps {
  onFile?: (file: File) => void;
  accept?: string;
  hint?: string;
  /** Existing image URL to preview. */
  previewUrl?: string;
  title?: string;
}

/**
 * Drag-and-drop / click upload area (frontend only — emits the File via onFile).
 */
export default function UploadBox({
  onFile,
  accept = "image/*",
  hint = "JPG, PNG, GIF, WEBP up to 2MB",
  previewUrl,
  title = "Click to upload",
}: UploadBoxProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const handleFile = (file?: File | null) => {
    if (!file) return;
    setLocalPreview(URL.createObjectURL(file));
    onFile?.(file);
  };

  const preview = localPreview ?? previewUrl;

  return (
    <Box>
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        sx={{
          cursor: "pointer",
          borderRadius: "14px",
          border: "2px dashed",
          borderColor: dragging ? GOLD : "divider",
          bgcolor: dragging ? alpha(GOLD, 0.06) : alpha(NAVY, 0.02),
          minHeight: 180,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
          transition: "all 0.2s ease",
          "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.04) },
        }}
      >
        {preview ? (
          <Box
            component="img"
            src={preview}
            alt="preview"
            sx={{ maxHeight: 120, maxWidth: "100%", borderRadius: 2, objectFit: "contain" }}
          />
        ) : (
          <>
            <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: alpha(NAVY, 0.5), mb: 1 }} />
            <Typography fontSize={14} fontWeight={700} color={NAVY}>
              {title}
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              {hint}
            </Typography>
          </>
        )}
      </Box>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CloudUploadOutlinedIcon />}
        sx={{ mt: 1.5, borderRadius: "10px" }}
        onClick={() => inputRef.current?.click()}
      >
        Browse Files
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </Box>
  );
}
